import {
  CardConfig,
  getAllCardConfigs,
  NestGroups,
  RowData,
} from "../nesting-summary-cards";
import { Injectable } from "@angular/core";
import type { NestingSummaryCardsComponent } from "../nesting-summary-cards.component";
import type { NestingCardsWorkflowDependencies } from "./nesting-cards-workflow-dependencies";

type NestingCardsFiltersWorkflowContext = Pick<
  NestingSummaryCardsComponent,
  | "nestGroupsList"
  | "activeNestGroup"
  | "nestGroupConfigs"
  | "appliedFiltersState"
  | "filters"
  | "cardConfigs"
  | "showLoader"
  | "filterData"
  | "totalCardConfigs"
  | "totalItems"
  | "currentPage"
  | "showMoveSkuPopup"
  | "selectedCardConfig"
  | "tempSelectedCardConfig"
  | "tempCardConfigs"
  | "filterPanel"
  | "onFilterApply"
>;
@Injectable({ providedIn: "root" })
export class NestingCardsFiltersWorkflow {
  toggleNestGroup(
    context: NestingCardsFiltersWorkflowContext,
    dependencies: NestingCardsWorkflowDependencies,
    group: NestGroups,
  ): void {
    context.nestGroupsList.forEach((g) => (g.isSelected = false));
    group.isSelected = true;
    context.activeNestGroup = group;
    const match = context.nestGroupConfigs.find(
      (cfg) => cfg.nestGroupValue === group.value,
    );
    if (!match) return;

    const hasActiveFilters = Object.keys(context.appliedFiltersState).some(
      (key) =>
        context.appliedFiltersState[key] &&
        context.appliedFiltersState[key].length > 0,
    );

    if (hasActiveFilters) {
      for (const key in context.appliedFiltersState) {
        if (context.filters[key]) {
          context.filters[key].selected = [...context.appliedFiltersState[key]];
        }
      }
      context.cardConfigs = match.cardConfigs.map((card) => ({
        ...card,
        rows: card.rows
          .filter((r) => dependencies.rowMatchesFilters(r))
          .map((r) => ({ ...r })),
      }));
    } else {
      context.cardConfigs = match.cardConfigs;
    }
  }
  setFilterData(
    context: NestingCardsFiltersWorkflowContext,
    dependencies: NestingCardsWorkflowDependencies,
  ): void {
    context.showLoader = true;
    const allRows = dependencies.getAllRowsFromConfigs();

    const brandSet = new Set<string>();
    const segmentSet = new Set<string>();
    const packTypeSet = new Set<string>();
    const packSizeSet = new Set<number>();
    const packQtySet = new Set<number>();

    for (const row of allRows) {
      if (row.brand) brandSet.add(row.brand);
      if (row.ptc_segment) segmentSet.add(row.ptc_segment);
      if (row.pack_type) packTypeSet.add(row.pack_type);
      if (row.pack_size !== undefined && row.pack_size !== null)
        packSizeSet.add(row.pack_size);
      if (row.pack_quantity !== undefined && row.pack_quantity !== null)
        packQtySet.add(row.pack_quantity);
    }

    context.filterData.brand = Array.from(brandSet);
    context.filterData.segment = Array.from(segmentSet);
    context.filterData.pack_type = Array.from(packTypeSet);
    context.filterData.pack_size = Array.from(packSizeSet);
    context.filterData.pack_qty = Array.from(packQtySet);

    context.filters.brand.values = [...context.filterData.brand];
    context.filters.segment.values = [...context.filterData.segment];
    context.filters.packType.values = [...context.filterData.pack_type];
    context.filters.packSize.values = [...context.filterData.pack_size];
    context.filters.packQty.values = [...context.filterData.pack_qty];
    context.filters.skuCount.values = ["1", "2", "3", "4", "5", "More than 5"];
    context.showLoader = false;
  }
  getAllRowsFromConfigs(
    context: NestingCardsFiltersWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
  ): RowData[] {
    const rows: RowData[] = [];
    for (const group of context.nestGroupConfigs) {
      for (const card of group.cardConfigs) {
        if (card.rows && card.rows.length > 0) {
          rows.push(...card.rows);
        }
      }
    }
    return rows;
  }
  filteredItems(
    context: NestingCardsFiltersWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
    key: keyof typeof context.filters,
  ): (string | number)[] {
    return context.filters[key].values.filter((item) =>
      item
        .toString()
        .toLowerCase()
        .includes(context.filters[key].search.toLowerCase()),
    );
  }
  toggleSelection(
    context: NestingCardsFiltersWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
    key: keyof typeof context.filters,
    item: string | number,
  ): void {
    const selected = context.filters[key].selected as (string | number)[];
    const index = selected.indexOf(item);
    if (index > -1) {
      selected.splice(index, 1);
    } else {
      selected.push(item);
    }
  }
  rowMatchesFilters(
    context: NestingCardsFiltersWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
    row: RowData | any,
  ): boolean {
    const f = context.filters;
    if (f.brand.selected.length > 0 && !f.brand.selected.includes(row.brand))
      return false;
    if (
      f.segment.selected.length > 0 &&
      !f.segment.selected.includes(row.ptc_segment)
    )
      return false;
    const packType = row.pack_type;
    if (
      f.packType.selected.length > 0 &&
      !f.packType.selected.includes(packType)
    )
      return false;
    const packSize = row.pack_size;
    if (
      f.packSize.selected.length > 0 &&
      !f.packSize.selected.some((sel) => sel === packSize)
    )
      return false;
    const packQty = row.pack_quantity;
    if (
      f.packQty.selected.length > 0 &&
      !f.packQty.selected.some((sel) => sel === packQty)
    )
      return false;
    return true;
  }
  onFilterApply(
    context: NestingCardsFiltersWorkflowContext,
    dependencies: NestingCardsWorkflowDependencies,
  ): void {
    for (const key in context.filters) {
      const selected = context.filters[key].selected.filter(
        (value, index, selected) =>
          value !== null &&
          value !== undefined &&
          String(value).trim() !== "" &&
          selected.findIndex((item) => String(item) === String(value)) ===
            index,
      );
      context.filters[key].selected = selected;
      context.appliedFiltersState[key] = [...selected];
    }

    const filteredByGroup: Record<string, CardConfig[]> = {};
    for (const group of context.nestGroupConfigs) {
      filteredByGroup[group.nestGroupValue] = group.cardConfigs.map((card) => ({
        ...card,
        rows: card.rows
          .filter((r) => dependencies.rowMatchesFilters(r))
          .map((r) => ({ ...r })),
      }));
    }

    if (context.filters.skuCount.selected.length > 0) {
      for (const groupValue in filteredByGroup) {
        filteredByGroup[groupValue] = filteredByGroup[groupValue].filter(
          (card) => dependencies.cardMatchesSkuCountFilter(card),
        );
      }
    }

    const active = context.nestGroupsList.find((g) => g.isSelected);
    if (active) {
      context.cardConfigs = (filteredByGroup[active.value] || []).map((c) => ({
        ...c,
        rows: c.rows.map((r) => ({ ...r })),
      }));
    } else {
      context.cardConfigs = [];
    }
    const allFilteredCards: CardConfig[] = [];
    for (const groupValue in filteredByGroup) {
      allFilteredCards.push(...filteredByGroup[groupValue]);
    }
    context.totalCardConfigs = allFilteredCards.filter(
      (card) => card.rows.length > 0,
    );
    context.totalItems = context.totalCardConfigs.length;
    context.currentPage = 1;
    if (context.showMoveSkuPopup && context.selectedCardConfig) {
      const activeGroupValue = context.activeNestGroup?.value || active?.value;
      const filteredForActive = activeGroupValue
        ? filteredByGroup[activeGroupValue] || []
        : [];
      const selectedNestNumber = context.selectedCardConfig.header.nestNumber;
      const selectedFiltered = filteredForActive.find(
        (c) => c.header.nestNumber === selectedNestNumber,
      );
      if (selectedFiltered) {
        context.tempSelectedCardConfig = {
          ...selectedFiltered,
          rows: selectedFiltered.rows.map((r) => ({ ...r })),
        };
      } else {
        context.tempSelectedCardConfig = {
          ...context.selectedCardConfig,
          rows: [],
        };
      }
      context.tempCardConfigs = filteredForActive
        .filter((c) => c.header.nestNumber !== selectedNestNumber)
        .map((c) => ({ ...c, rows: c.rows.map((r) => ({ ...r })) }));
    }
    context.filterPanel.hide();
  }
  clearFilter(
    context: NestingCardsFiltersWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
  ): void {
    for (const k of Object.keys(
      context.filters,
    ) as (keyof typeof context.filters)[]) {
      context.filters[k].selected = [];
      context.filters[k].search = "";
    }
    context.appliedFiltersState = {};

    const active = context.nestGroupsList.find((g) => g.isSelected);
    if (active) {
      const groupCfg = context.nestGroupConfigs.find(
        (g) => g.nestGroupValue === active.value,
      );
      if (groupCfg) {
        context.cardConfigs = groupCfg.cardConfigs.map((card) => ({
          ...card,
          rows: card.rows.map((r) => ({ ...r })),
        }));
      } else {
        context.cardConfigs = [];
      }
    } else {
      context.cardConfigs = [];
    }
    context.totalCardConfigs = getAllCardConfigs(context.nestGroupConfigs);
    context.totalItems = context.totalCardConfigs.length;
    context.currentPage = 1;
    if (context.showMoveSkuPopup && context.selectedCardConfig) {
      const activeGroupValue = context.activeNestGroup?.value || active?.value;
      const groupCfg = context.nestGroupConfigs.find(
        (g) => g.nestGroupValue === activeGroupValue,
      );
      if (groupCfg) {
        const selectedNestNumber = context.selectedCardConfig.header.nestNumber;
        const selectedCard = groupCfg.cardConfigs.find(
          (c) => c.header.nestNumber === selectedNestNumber,
        );
        if (selectedCard) {
          context.tempSelectedCardConfig = {
            ...selectedCard,
            rows: selectedCard.rows.map((r) => ({ ...r })),
          };
        } else {
          context.tempSelectedCardConfig = {
            ...context.selectedCardConfig,
            rows: [],
          };
        }
        context.tempCardConfigs = groupCfg.cardConfigs
          .filter((c) => c.header.nestNumber !== selectedNestNumber)
          .map((c) => ({ ...c, rows: c.rows.map((r) => ({ ...r })) }));
      } else {
        context.tempSelectedCardConfig = null;
        context.tempCardConfigs = [];
      }
    }
  }
  cardMatchesSkuCountFilter(
    context: NestingCardsFiltersWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
    card: CardConfig,
  ): boolean {
    const skuCount = card.rows.length;
    const selectedCounts = context.filters.skuCount.selected;

    for (const selected of selectedCounts) {
      if (selected === "More than 5" && skuCount > 5) {
        return true;
      } else if (Number(selected) === skuCount) {
        return true;
      }
    }
    return false;
  }
  removeFilter(
    context: NestingCardsFiltersWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
    key: string,
    value: string,
  ): void {
    if (context.appliedFiltersState[key]) {
      context.appliedFiltersState[key] = context.appliedFiltersState[
        key
      ].filter((v) => String(v) !== value);
    }
    const filter = context.filters[key];
    if (filter) {
      filter.selected = filter.selected.filter((v) => String(v) !== value);
      context.onFilterApply();
    }
  }
  reapplyFiltersAfterDataRefresh(
    context: NestingCardsFiltersWorkflowContext,
    dependencies: NestingCardsWorkflowDependencies,
  ): void {
    for (const key in context.appliedFiltersState) {
      if (context.filters[key]) {
        context.filters[key].selected = [...context.appliedFiltersState[key]];
      }
    }

    const filteredByGroup: Record<string, CardConfig[]> = {};
    for (const group of context.nestGroupConfigs) {
      filteredByGroup[group.nestGroupValue] = group.cardConfigs.map((card) => ({
        ...card,
        rows: card.rows
          .filter((r) => dependencies.rowMatchesFilters(r))
          .map((r) => ({ ...r })),
      }));
    }

    if (context.filters.skuCount.selected.length > 0) {
      for (const groupValue in filteredByGroup) {
        filteredByGroup[groupValue] = filteredByGroup[groupValue].filter(
          (card) => dependencies.cardMatchesSkuCountFilter(card),
        );
      }
    }

    const active = context.nestGroupsList.find((g) => g.isSelected);
    if (active) {
      context.cardConfigs = (filteredByGroup[active.value] || []).map((c) => ({
        ...c,
        rows: c.rows.map((r) => ({ ...r })),
      }));
    } else {
      context.cardConfigs = [];
    }
    const allFilteredCards: CardConfig[] = [];
    for (const groupValue in filteredByGroup) {
      allFilteredCards.push(...filteredByGroup[groupValue]);
    }
    context.totalCardConfigs = allFilteredCards.filter(
      (card) => card.rows.length > 0,
    );
    context.totalItems = context.totalCardConfigs.length;
    context.currentPage = 1;
  }
}
